import React, { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { postFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { BellIcon, PaperPlaneIcon } from '@radix-ui/react-icons'

type UpdateAnnouncement = {
  id: string
  items: string[]
  docUrl: string | null
  publishedAt: number
  authorId: string
  authorName: string
}

type UpdateAnnouncementInbox = {
  list: UpdateAnnouncement[]
  unread: UpdateAnnouncement[]
  showBadge: boolean
  latest: UpdateAnnouncement | null
}

const INBOX_KEY = ['/feishu/update-announcements']

const formatPublishedAt = (ms: number) =>
  new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(ms)

const errorMessage = (err: unknown) =>
  err instanceof Error ? err.message : '请稍后重试'

function AnnouncementItems({ items }: { items: string[] }) {
  return (
    <ol className="list-decimal space-y-1 pl-5 text-sm">
      {items.map((item, index) => (
        <li key={`${index}-${item}`}>{item}</li>
      ))}
    </ol>
  )
}

function openDoc(docUrl: string) {
  window.open(docUrl, '_blank', 'noopener,noreferrer')
}

export default function UpdateAnnouncements({
  canPublish,
}: {
  canPublish: boolean
}) {
  const { toast } = useToast()
  const { data, error, mutate } = useSWR<UpdateAnnouncementInbox>(INBOX_KEY, {
    revalidateOnFocus: false,
  })
  const { trigger: createAnnouncement, isMutating: creating } = useSWRMutation(
    '/feishu/update-announcements',
    postFetcher,
  )
  const { trigger: sendAnnouncement, isMutating: sending } = useSWRMutation(
    '/feishu/update-announcement/send',
    postFetcher,
  )

  const [popupOpen, setPopupOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [creatingOpen, setCreatingOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [content, setContent] = useState('')
  const [docUrl, setDocUrl] = useState('')
  const [deferredIds, setDeferredIds] = useState(() => new Set<string>())
  const [markingRead, setMarkingRead] = useState(false)
  const readingRef = useRef(false)

  const unread = data?.unread ?? []
  const list = data?.list ?? []
  const latest = data?.latest ?? null
  const visibleUnread = unread.filter((item) => !deferredIds.has(item.id))
  const unreadIds = new Set(unread.map((item) => item.id))
  const showBadge = unread.length > 0
  const visibleKey = visibleUnread.map((item) => item.id).join(',')
  const popupItemsRef = useRef<UpdateAnnouncement[]>(visibleUnread)
  if (visibleUnread.length > 0) popupItemsRef.current = visibleUnread
  const popupItems = visibleUnread.length > 0 ? visibleUnread : popupItemsRef.current

  useEffect(() => {
    if (visibleKey) setPopupOpen(true)
  }, [visibleKey])

  const defer = (ids: string[]) => {
    setDeferredIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  const rememberRead = (id: string) => {
    mutate(
      (current) => {
        if (!current) return current
        const nextUnread = (current.unread || []).filter((item) => item.id !== id)
        return {
          ...current,
          unread: nextUnread,
          showBadge: nextUnread.length > 0,
        }
      },
      { revalidate: false },
    )
  }

  const readAnnouncement = async (id: string) => {
    if (readingRef.current) return false
    readingRef.current = true
    setMarkingRead(true)
    try {
      await postFetcher(`/feishu/update-announcements/${id}/read`, { arg: {} })
      rememberRead(id)
      return true
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '状态保存失败',
        description: errorMessage(err),
      })
      return false
    } finally {
      readingRef.current = false
      setMarkingRead(false)
    }
  }

  const confirmAnnouncement = async (announcement: UpdateAnnouncement) => {
    await readAnnouncement(announcement.id)
  }

  const viewAnnouncement = (announcement: UpdateAnnouncement) => {
    if (!announcement.docUrl) return
    openDoc(announcement.docUrl)
    if (!unreadIds.has(announcement.id)) return
    void readAnnouncement(announcement.id)
  }

  const closePopup = () => {
    defer(visibleUnread.map((item) => item.id))
    setPopupOpen(false)
  }

  const openHistory = () => {
    setHistoryOpen(true)
  }

  const publish = async () => {
    try {
      await createAnnouncement({ content, docUrl })
      setContent('')
      setDocUrl('')
      setCreatingOpen(false)
      await mutate()
      toast({ title: '已发布', description: '这条公告会提示还没看过的人' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '发布失败',
        description: errorMessage(err),
      })
    }
  }

  const sendLatest = async () => {
    try {
      await sendAnnouncement({})
      setSendOpen(false)
      toast({
        title: '已发送',
        description: '更新公告已投递到画师群与运营群',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '发送失败',
        description: errorMessage(err),
      })
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="whitespace-nowrap text-primary hover:text-primary/80"
        onClick={openHistory}
      >
        更新公告
        {showBadge ? (
          <BellIcon className="ml-1 h-4 w-4 text-red-500" aria-label="有未读公告" />
        ) : null}
      </Button>
      {canPublish ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-primary hover:text-primary/80"
          title={latest ? '把最新公告发到飞书群' : '本月还没有更新公告'}
          aria-label="把最新公告发到飞书群"
          disabled={!latest || sending}
          onClick={() => setSendOpen(true)}
        >
          <PaperPlaneIcon className="h-4 w-4 -rotate-45" />
        </Button>
      ) : null}

      <Dialog
        open={popupOpen && visibleUnread.length > 0}
        onOpenChange={(open) => {
          if (!open) closePopup()
        }}
      >
        <DialogContent
          className="max-h-[80vh] overflow-y-auto outline-none focus:outline-none focus-visible:outline-none data-[state=open]:!animate-none data-[state=closed]:!animate-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>功能更新公告</DialogTitle>
            <DialogDescription>
              点确认，或打开文档，这条才算已读。稍后和关闭都不算，下次进来还会出现。
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            {popupItems.map((announcement) => (
              <div
                key={announcement.id}
                className="space-y-2 border-b pb-4 last:border-b-0"
              >
                <div className="text-xs text-muted-foreground">
                  {formatPublishedAt(announcement.publishedAt)}
                  {announcement.authorName ? ` · ${announcement.authorName}` : ''}
                </div>
                <AnnouncementItems items={announcement.items} />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    disabled={markingRead}
                    onClick={() => void confirmAnnouncement(announcement)}
                  >
                    确认
                  </Button>
                  {announcement.docUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={markingRead}
                      onClick={() => void viewAnnouncement(announcement)}
                    >
                      查看更新内容
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={closePopup}>
              稍后
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>更新公告</DialogTitle>
            <DialogDescription>近一个月的后台更新</DialogDescription>
          </DialogHeader>
          {canPublish ? (
            <Button type="button" onClick={() => setCreatingOpen(true)}>
              新建公告
            </Button>
          ) : null}
          {error ? (
            <p className="text-sm text-muted-foreground">公告加载失败，请稍后重试</p>
          ) : list.length ? (
            <div className="space-y-4">
              {list.map((announcement) => (
                <div key={announcement.id} className="space-y-2 border-b pb-4 last:border-b-0">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {unreadIds.has(announcement.id) ? (
                      <BellIcon className="h-3.5 w-3.5 text-red-500" aria-label="未读" />
                    ) : null}
                    <span>
                      {formatPublishedAt(announcement.publishedAt)}
                      {announcement.authorName ? ` · ${announcement.authorName}` : ''}
                    </span>
                  </div>
                  <AnnouncementItems items={announcement.items} />
                  {announcement.docUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={markingRead}
                      onClick={() => void viewAnnouncement(announcement)}
                    >
                      查看
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">近一个月暂无更新公告</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={creatingOpen} onOpenChange={setCreatingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建更新公告</DialogTitle>
            <DialogDescription>
              每行写一条，用运营能看懂的话说明后台哪里变了。配图放在飞书文档里，把链接贴在下面，打开时是新标签页。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="announcement-content">更新说明</Label>
            <Textarea
              id="announcement-content"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder={'搜索热词按大家真实搜过的词来排\nWebImages 卡片上能看到使用次数'}
              className="min-h-[140px]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="announcement-doc">飞书文档链接（可选）</Label>
            <Input
              id="announcement-doc"
              value={docUrl}
              onChange={(event) => setDocUrl(event.target.value)}
              placeholder="https://stickerstyle.feishu.cn/docx/..."
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreatingOpen(false)}
            >
              取消
            </Button>
            <Button type="button" disabled={creating} onClick={() => void publish()}>
              {creating ? '发布中…' : '发布'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>发送更新公告到飞书群？</DialogTitle>
            <DialogDescription>
              将把本月最新一条公告发到画师群与运营群。
            </DialogDescription>
          </DialogHeader>
          {latest ? <AnnouncementItems items={latest.items} /> : null}
          {latest?.docUrl ? (
            <p className="break-all text-sm text-muted-foreground">{latest.docUrl}</p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSendOpen(false)}>
              取消
            </Button>
            <Button type="button" disabled={sending} onClick={() => void sendLatest()}>
              {sending ? '发送中…' : '确认发送'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
